import { useState } from "react";
import type { Deal } from "../types";
import { isCloud } from "../lib/supabase";
import { saveDeal } from "../lib/deals";
import { listMembers } from "../lib/team";
import { listBuyBoxes } from "../lib/buybox";
import { listScenarios } from "../lib/scenarios";
import { buildBackup, dealsToCsv, downloadText } from "../lib/exportData";
import { syncDealToGhl, testGhl } from "../lib/ghl";

/**
 * Two panels, in deliberate order:
 *   1. YOUR DATA — Beacon's Postgres is the source of truth; export it anytime.
 *   2. CRM sync — GoHighLevel is a downstream mirror, never authoritative.
 * The ordering is the point: you own the database; the CRM only gets a copy.
 */
export function DataAndCrm({ deals }: { deals: Deal[] }) {
  return (
    <>
      <OwnYourData deals={deals} />
      <CrmSync deals={deals} />
    </>
  );
}

function OwnYourData({ deals }: { deals: Deal[] }) {
  const [busy, setBusy] = useState(false);
  const stamp = () => new Date().toISOString().slice(0, 10);

  async function exportJson() {
    setBusy(true);
    try {
      const [team, buyBoxes, scenarios] = await Promise.all([listMembers(), listBuyBoxes(), listScenarios()]);
      const backup = { ...buildBackup(deals, team, buyBoxes, scenarios), exportedAt: new Date().toISOString() };
      downloadText(`beacon-backup-${stamp()}.json`, JSON.stringify(backup, null, 2));
    } finally { setBusy(false); }
  }
  function exportCsv() {
    downloadText(`beacon-deals-${stamp()}.csv`, dealsToCsv(deals), "text/csv");
  }

  return (
    <div className="card pad" style={{ marginBottom: 14, borderLeft: "4px solid var(--forest)" }}>
      <div className="eyebrow">Your data — you own it</div>
      <p style={{ fontSize: 13.5, color: "#37413c", margin: "8px 0 12px", lineHeight: 1.6 }}>
        Beacon's <b>own database is the source of truth</b> — your deals, team, buy boxes, and scenarios live in a
        Postgres database <b>you control</b> ({isCloud() ? "your Supabase project" : "this browser in local mode; your Supabase project in cloud mode"}).
        No CRM or third party is the system of record. Proof: take everything with you, any time, in an open format.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn btn-p btn-sm" onClick={exportJson} disabled={busy}>{busy ? "Gathering…" : "⭳ Export everything (JSON)"}</button>
        <button className="btn btn-g btn-sm" onClick={exportCsv} disabled={!deals.length}>⭳ Export deals (CSV)</button>
      </div>
      <div className="note" style={{ marginTop: 8 }}>
        The JSON is a complete, restorable backup of your workspace ({deals.length} deal{deals.length === 1 ? "" : "s"} + team + buy boxes + scenarios). Nothing is locked in.
      </div>
    </div>
  );
}

function CrmSync({ deals }: { deals: Deal[] }) {
  const [test, setTest] = useState<{ state: "idle" | "run" | "ok" | "off" | "err"; msg?: string }>({ state: "idle" });
  const [sync, setSync] = useState<{ running: boolean; done: number; ok: number; msg?: string }>({ running: false, done: 0, ok: 0 });

  async function runTest() {
    setTest({ state: "run" });
    const r = await testGhl();
    if (!r.configured) setTest({ state: "off", msg: r.error });
    else if (r.error) setTest({ state: "err", msg: r.error });
    else setTest({ state: "ok", msg: (r as { locationName?: string }).locationName });
  }

  async function syncAll() {
    setSync({ running: true, done: 0, ok: 0 });
    let ok = 0;
    for (let i = 0; i < deals.length; i++) {
      const r = await syncDealToGhl(deals[i]);
      if (!r.configured) { setSync({ running: false, done: i, ok, msg: r.error }); return; }
      if (!r.error && (r.contactId || r.opportunityId)) {
        ok++;
        await saveDeal({ ...deals[i], ghlContactId: r.contactId, ghlOpportunityId: r.opportunityId, ghlSyncedAt: new Date().toISOString() });
      }
      setSync({ running: true, done: i + 1, ok });
    }
    setSync({ running: false, done: deals.length, ok, msg: `Pushed ${ok}/${deals.length} to GoHighLevel.` });
  }

  return (
    <div className="card pad" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="eyebrow">CRM sync — GoHighLevel <span style={{ color: "var(--muted)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· downstream mirror</span></div>
        <span className={"tag " + (test.state === "ok" ? "t-pass" : "t-watch")}>{test.state === "ok" ? "Connected" : "Not connected"}</span>
      </div>
      <p style={{ fontSize: 13, color: "#37413c", margin: "8px 0 12px", lineHeight: 1.6 }}>
        A <b>one-way push</b> of each deal into GHL as a Contact (property data on its custom fields) + an Opportunity in
        your <b>Beacon Deals</b> pipeline. Beacon keeps the keys and stays the source of truth — GHL only ever holds a
        copy for outreach &amp; pipeline. Drop GHL any day and nothing here changes.
      </p>
      {!isCloud() ? (
        <div className="flag warn"><span>◐</span><div>CRM sync runs in cloud mode — the GHL token lives server-side in the <span className="mono">ghl-sync</span> Edge Function.</div></div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn btn-g btn-sm" onClick={runTest} disabled={test.state === "run"}>{test.state === "run" ? "Testing…" : "Test connection"}</button>
            <button className="btn btn-p btn-sm" onClick={syncAll} disabled={sync.running || !deals.length}>
              {sync.running ? `Syncing… ${sync.done}/${deals.length}` : `⇪ Sync all ${deals.length} deals to GHL`}
            </button>
          </div>
          {test.state === "off" && <div className="flag warn" style={{ marginTop: 10 }}><span>◐</span><div>Token not set yet. Add it: <span className="mono">supabase secrets set GHL_TOKEN=… GHL_LOCATION_ID=…</span></div></div>}
          {test.state === "ok" && <div className="flag ok" style={{ marginTop: 10 }}><span>✓</span><div>Connected to <b>{test.msg || "your GHL location"}</b>.</div></div>}
          {test.state === "err" && <div className="flag bad" style={{ marginTop: 10 }}><span>!</span><div>{test.msg}</div></div>}
          {sync.msg && <div className={"flag " + (sync.ok ? "ok" : "warn")} style={{ marginTop: 10 }}><span>{sync.ok ? "✓" : "◐"}</span><div>{sync.msg}</div></div>}
        </>
      )}
      <div className="note" style={{ marginTop: 10 }}>
        Setup: create a <b>Private Integration token</b> in GHL (Settings → Private Integrations; grant Contacts + Opportunities read/write) and a <b>Beacon Deals</b> pipeline, then set the two secrets. First real sync validates the field mapping.
      </div>
    </div>
  );
}
